/**
 * Sentry configuration for error tracking and performance monitoring
 *
 * This module initializes Sentry with integrations for:
 * - React error boundaries
 * - Browser performance tracking
 * - TanStack Router navigation tracking
 * - React Query performance monitoring
 */

import * as Sentry from '@sentry/react'
import { env } from './env'

/**
 * Initialize Sentry with application-specific configuration
 *
 * IMPORTANT: This should be called as early as possible in the application lifecycle,
 * before React renders. See main.tsx for proper initialization.
 */
export function initializeSentry() {
    // Only initialize if DSN is provided
    if (!env.SENTRY_DSN) {
        console.warn(
            '[Sentry] DSN not configured - error tracking disabled. Set VITE_SENTRY_DSN to enable.'
        )
        return
    }

    Sentry.init({
        dsn: env.SENTRY_DSN,

        // Environment configuration
        environment: env.ENVIRONMENT,

        // Send default PII (Personally Identifiable Information)
        // This includes IP addresses and user agent strings
        sendDefaultPii: true,

        // Integrations for enhanced tracking
        integrations: [
            // Browser tracing for performance monitoring
            Sentry.browserTracingIntegration({
                // Enable performance tracking for navigation
                enableInp: true, // Track Interaction to Next Paint
            }),

            // Replay integration for session replay (optional - can be enabled later)
            // Sentry.replayIntegration({
            //   maskAllText: false,
            //   blockAllMedia: false,
            // }),
        ],

        // Performance Monitoring
        tracesSampleRate: env.ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev/staging

        // Set tracePropagationTargets to control distributed tracing
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/api\.whatthefuckareyouwearing\.com/,
            /^https:\/\/staging-api\.whatthefuckareyouwearing\.com/,
        ],

        // Session Replay (optional - disabled by default)
        // replaysSessionSampleRate: 0.1, // Sample 10% of sessions
        // replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors

        // Enable console logs to be sent to Sentry as breadcrumbs
        beforeBreadcrumb(breadcrumb) {
            // Filter out noisy breadcrumbs
            if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
                // Don't send debug console.logs in production
                if (env.ENVIRONMENT === 'production') {
                    return null
                }
            }
            return breadcrumb
        },

        // Filter out unwanted errors
        beforeSend(event, hint) {
            const error = hint.originalException

            // Filter out network errors that are expected
            if (error && typeof error === 'object' && 'message' in error) {
                const message = String(error.message).toLowerCase()

                // Ignore known browser extension errors
                if (
                    message.includes('extension') ||
                    message.includes('chrome-extension')
                ) {
                    return null
                }

                // Ignore ResizeObserver errors (known React 18 issue)
                if (message.includes('resizeobserver')) {
                    return null
                }

                // Ignore Firebase network errors (these are handled separately)
                if (message.includes('firebase') && message.includes('network')) {
                    return null
                }
            }

            return event
        },

        // Add additional context to all events
        initialScope: {
            tags: {
                app_version: '1.0.0', // Update this from package.json
            },
        },
    })

    console.log(
        `[Sentry] Initialized for ${env.ENVIRONMENT} environment (DSN: ${env.SENTRY_DSN?.slice(0, 20)}...)`
    )
}

/**
 * Helper to set user context in Sentry
 * Called from UserContext when user logs in/out
 */
export function setSentryUser(user: {
    id: string
    email?: string | null
    username?: string | null
}) {
    Sentry.setUser({
        id: user.id,
        email: user.email ?? undefined,
        username: user.username ?? undefined,
    })
}

/**
 * Helper to clear user context in Sentry
 * Called from UserContext when user logs out
 */
export function clearSentryUser() {
    Sentry.setUser(null)
}

/**
 * Helper to manually capture exceptions
 * Use this for caught errors that should still be tracked
 */
export function captureException(error: unknown, context?: Record<string, unknown>) {
    Sentry.captureException(error, {
        extra: context,
    })
}

/**
 * Helper to manually capture messages
 * Use this for non-error events that should be logged
 */
export function captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: Record<string, unknown>
) {
    Sentry.captureMessage(message, {
        level,
        extra: context,
    })
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, unknown>
) {
    Sentry.addBreadcrumb({
        message,
        category,
        data,
        level: 'info',
    })
}

// Re-export Sentry for direct access when needed
export { Sentry }
